import { baseApi } from "./baseApi";
import {
  FactoringDebtor,
  FactoringDebtorPatchPayload,
  FactoringDebtorPayload,
  PaginatedResponse,
  ResponseData,
} from "@interfaces/index";

/**
 * Deudores de factoraje de una solicitud: `/solicitudes/{solicitud_id}/deudores_factoraje`.
 * El API no valida que la suma de montos no exceda la línea: ese límite lo cuida el front.
 */
const factoringApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** Solo devuelve los activos, del más viejo al más nuevo. */
    getFactoringDebtors: build.query<FactoringDebtor[], { loanApplicationId: string; page?: number; size?: number }>({
      query: ({ loanApplicationId, page = 1, size = 100 }) =>
        `solicitudes/${loanApplicationId}/deudores_factoraje?page=${page}&size=${size}`,
      transformResponse: (response: PaginatedResponse<FactoringDebtor>) => response.data.data,
    }),
    getFactoringDebtorById: build.query<FactoringDebtor, { loanApplicationId: string; debtorId: string }>({
      query: ({ loanApplicationId, debtorId }) => `solicitudes/${loanApplicationId}/deudores_factoraje/${debtorId}`,
      transformResponse: (response: ResponseData<FactoringDebtor>) => response.data,
    }),
    postFactoringDebtor: build.mutation<FactoringDebtor, { loanApplicationId: string; body: FactoringDebtorPayload }>({
      query: ({ loanApplicationId, body }) => ({
        url: `solicitudes/${loanApplicationId}/deudores_factoraje`,
        method: "POST",
        body,
      }),
      transformResponse: (response: ResponseData<FactoringDebtor>) => response.data,
    }),
    /** Parcial: manda solo lo que cambia. */
    patchFactoringDebtor: build.mutation<
      FactoringDebtor,
      { loanApplicationId: string; debtorId: string; body: FactoringDebtorPatchPayload }
    >({
      query: ({ loanApplicationId, debtorId, body }) => ({
        url: `solicitudes/${loanApplicationId}/deudores_factoraje/${debtorId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: ResponseData<FactoringDebtor>) => response.data,
    }),
    /** Borrado lógico: queda con `active: false` y deja de listarse. */
    deleteFactoringDebtor: build.mutation<{ id: string }, { loanApplicationId: string; debtorId: string }>({
      query: ({ loanApplicationId, debtorId }) => ({
        url: `solicitudes/${loanApplicationId}/deudores_factoraje/${debtorId}`,
        method: "DELETE",
      }),
      transformResponse: (response: ResponseData<{ id: string }>) => response.data,
    }),
  }),
});

export const {
  useLazyGetFactoringDebtorsQuery,
  useLazyGetFactoringDebtorByIdQuery,
  usePostFactoringDebtorMutation,
  usePatchFactoringDebtorMutation,
  useDeleteFactoringDebtorMutation,
} = factoringApi;
